'use client';

import { c as _c } from "react/compiler-runtime";
import { useId, useState } from 'react';
import Morpho from '../../icons/Morpho';
import { StaticAlertDialogContent, StaticAlertDialogTitle } from '../_base/AlertDialog';
import { Button } from '../_base/Button';
import { Checkbox } from '../_base/Checkbox';
import { TERMS_OF_USE_ACCEPTED_EVENT, setTermsOfUseAcceptedCookie } from './termsOfUseCookie';
const RISKS_URL = 'https://docs.morpho.org/developers/earn/get-started/#risk-considerations';
const TERMS_OF_USE_URL = 'https://morpho.org/terms-of-use/';
const PRIVACY_POLICY_URL = 'https://morpho.org/privacy-policy';
type ExternalLinkProps = {
  href: string;
  children: React.ReactNode;
};
function ExternalLink(t0) {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== href) {
    t1 = <a href={href} className="underline" target="_blank" rel="noopener noreferrer">{children}</a>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type TermsOfUseNoticeProps = {
  accepted: boolean;
  onAccept?: () => void;
};

/**
 * Legal's Terms of Use consent, shared by every Morpho app. Blocks the app until the user ticks
 * the checkbox and clicks Continue. Built on the static (non-portal) AlertDialog primitives so
 * it server-renders with the first HTML. Pass `accepted` from the `TERMS_OF_USE_COOKIE`
 * cookie read on the server.
 */
export function TermsOfUseNotice(t0) {
  const $ = _c(32);
  const {
    accepted: initialAccepted,
    onAccept
  } = t0;
  const [accepted, setAccepted] = useState(initialAccepted);
  const [checked, setChecked] = useState(false);
  const titleId = useId();
  const descriptionId = useId();
  const checkboxId = useId();
  if (accepted) {
    return null;
  }
  let t1;
  if ($[0] !== onAccept) {
    t1 = function handleContinue() {
      setTermsOfUseAcceptedCookie();
      window.dispatchEvent(new Event(TERMS_OF_USE_ACCEPTED_EVENT));
      setAccepted(true);
      onAccept?.();
    };
    $[0] = onAccept;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleContinue = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div aria-hidden="true" className="flex h-40 w-full shrink-0 items-center justify-center bg-[radial-gradient(148%_170%_at_-6%_114%,#5792ff_0%,#79a8ff_50%,#86b1ff_100%)]"><Morpho className="size-14" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== titleId) {
    t3 = <StaticAlertDialogTitle id={titleId} className="text-title-xs text-popover-foreground">Before you continue</StaticAlertDialogTitle>;
    $[3] = titleId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p>The Morpho App is a non-custodial interface operated by the Morpho Association to give you access to the Morpho protocols. Morpho Association does not hold your assets, does not provide financial advice, and is not a regulated financial institution. Using the protocols involves risks, including smart contract failures, oracle failures, and{" "}<ExternalLink href={RISKS_URL}>other risks</ExternalLink>. You may lose all the assets you supply or deposit.</p>;
    t5 = <p>Before your first interaction, your wallet may ask you to approve smart contracts. Check each approval before you sign, because onchain transactions are irreversible.</p>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[7] !== descriptionId) {
    t6 = <div id={descriptionId} className="text-foreground flex flex-col gap-3 text-xs">{t4}{t5}</div>;
    $[7] = descriptionId;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = value => setChecked(value === true);
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== checkboxId || $[11] !== checked) {
    t8 = <Checkbox id={checkboxId} data-testid="terms-of-use-checkbox" autoFocus={true} checked={checked} onCheckedChange={t7} className="mt-0.5" />;
    $[10] = checkboxId;
    $[11] = checked;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ExternalLink href={TERMS_OF_USE_URL}>Terms of Use</ExternalLink>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== checkboxId) {
    t11 = <label htmlFor={checkboxId} className="text-foreground cursor-pointer text-xs">I agree to the {t9}{" &"}{" "}{t10}, and confirm that I am not subject to sanctions, not located in a Restricted Territory, and responsible for making sure my use of the Morpho App is lawful where I am.</label>;
    $[15] = checkboxId;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t8) {
    t12 = <div className="bg-accent flex items-start gap-3 rounded-xl p-3">{t8}{t11}</div>;
    $[17] = t11;
    $[18] = t8;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = !checked;
  let t14;
  if ($[20] !== handleContinue || $[21] !== t13) {
    t14 = <Button size="lg" data-testid="terms-of-use-continue" disabled={t13} onClick={handleContinue}>Continue</Button>;
    $[20] = handleContinue;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t12 || $[24] !== t14 || $[25] !== t3 || $[26] !== t6) {
    t15 = <div className="p-lg flex flex-col gap-5">{t3}{t6}{t12}{t14}</div>;
    $[23] = t12;
    $[24] = t14;
    $[25] = t3;
    $[26] = t6;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== descriptionId || $[29] !== t15 || $[30] !== titleId) {
    t16 = <StaticAlertDialogContent aria-labelledby={titleId} aria-describedby={descriptionId} className="gap-0 p-0 sm:max-w-[500px]">{t2}{t15}</StaticAlertDialogContent>;
    $[28] = descriptionId;
    $[29] = t15;
    $[30] = titleId;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}